// apps/admin/src/api/credit.api.ts
//
// Phase 3.4b Wave 7 Task 15 — admin-side credit thin API wrappers
// (grant / edit / revoke + per-merchant history + cross-merchant list).
//
// Per CONTEXT.md
//   • admin grants/edits/revokes credit per merchant; period
//                + limit + 5 toggles; lifecycle active→overdue→expired/revoked.
//   • Idempotency-Key (UUID v4) on every mutation.
//   • only admins with `credit:manage` (mutations)
//                `credit:read` (reads) reach this surface; FE assumes
//                button-level RBAC gating already happened upstream.
//
// Endpoint surface (Wave 4 Task 8 contract):
//   • POST   /admin/merchants/:merchantId/credit                  → grantCredit
//   • PATCH  /admin/merchants/:merchantId/credit/:creditId        → editCredit
//   • POST   /admin/merchants/:merchantId/credit/:creditId/revoke → revokeCredit
//   • GET    /admin/merchants/:merchantId/credit/history          → getMerchantCreditHistory
//   • GET    /admin/credits                                        → listAdminCredits
//
// Defensive shape: openapi-fetch's generated `paths` type may not yet
// know about the credit routes until the spec is regenerated, so we
// cast through `LooseClient` (same pattern as merchant.api.ts).

import { apiClient } from '@printway/shared/api';
import type {
  CreateCreditInput,
  UpdateCreditInput,
  CreditPayload,
  CreditStatus,
} from '@printway/shared';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from '../pages/billing/billing-shared.js';

// ---------------------------------------------------------------------------
// Wire types — slim mirrors of API-CONTRACTS shapes the FE consumes.
// ---------------------------------------------------------------------------

/**
 * Row shape for GET /admin/merchants/:merchantId/credit/history. Each row
 * carries the full credit payload + a server-computed debt snapshot for
 * direct table render (no extra round-trip).
 */
export interface MerchantCreditHistoryRow {
  credit: CreditPayload;
  debtCents: number;
  remainingCents: number;
  daysToExpiry: number;
  /** Creator (admin) identity, server-populated for the "Created by" column. */
  createdByEmail?: string | null;
  createdByName?: string | null;
}

export interface MerchantCreditHistoryResponse {
  rows: MerchantCreditHistoryRow[];
  nextCursor: string | null;
  hasMore: boolean;
  total: number;
}

export interface MerchantCreditHistoryFilters {
  status?: CreditStatus;
  cursor?: string;
  limit?: number;
  dir?: 'asc' | 'desc';
}

/** Cross-merchant admin row shape — joins minimal merchant identity. */
export interface AdminCreditRow {
  credit: CreditPayload;
  merchant: {
    merchantId: string;
    email: string;
    displayName: string;
  };
  debtCents: number;
  remainingCents: number;
  daysToExpiry: number;
}

export interface AdminCreditsListResponse {
  rows: AdminCreditRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdminCreditsListFilters {
  merchantId?: string;
  status?: CreditStatus;
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: 'createdAt' | 'limitCents' | 'endDate';
  dir?: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/admin/merchants/:merchantId/credit — grant a new credit.
 * Auto-generates Idempotency-Key UUID v4 per call. Returns the
 * full persisted CreditPayload.
 */
export async function grantCredit(
  merchantId: string,
  dto: CreateCreditInput,
): Promise<CreditPayload> {
  const c = apiClient() as unknown as LooseClient;
  const path = `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/credit`;
  const { data, error, response } = await c.POST(path, {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
    body: dto,
  });
  if (error || !response.ok) {
    throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
  }
  return unwrap<CreditPayload>(data);
}

/**
 * PATCH /api/v1/admin/merchants/:merchantId/credit/:creditId — edit
 * limit (increase-only server-enforced) / period / options. startDate is
 * immutable (server returns CREDIT_START_DATE_IMMUTABLE if attempted).
 */
export async function editCredit(
  merchantId: string,
  creditId: string,
  dto: UpdateCreditInput,
): Promise<CreditPayload> {
  const c = apiClient() as unknown as LooseClient;
  const path = `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/credit/${encodeURIComponent(creditId)}`;
  const { data, error, response } = await c.PATCH(path, {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
    body: dto,
  });
  if (error || !response.ok) {
    throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
  }
  return unwrap<CreditPayload>(data);
}

/**
 * POST /api/v1/admin/merchants/:merchantId/credit/:creditId/revoke —
 * manual revoke. Reason is mandatory (server requires 10-500 chars).
 *
 * Exposed here as a sibling to grant/edit even though Task 16's revoke
 * dialog is the primary consumer — keeping the trio co-located simplifies
 * call sites that already imported `credit.api`.
 */
export async function revokeCredit(
  merchantId: string,
  creditId: string,
  reason: string,
): Promise<CreditPayload> {
  const c = apiClient() as unknown as LooseClient;
  const path = `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/credit/${encodeURIComponent(creditId)}/revoke`;
  const { data, error, response } = await c.POST(path, {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
    body: { reason },
  });
  if (error || !response.ok) {
    throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
  }
  return unwrap<CreditPayload>(data);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

function buildHistoryQs(filters: MerchantCreditHistoryFilters): string {
  const sp = new URLSearchParams();
  if (filters.status) sp.set('status', filters.status);
  if (filters.cursor) sp.set('cursor', filters.cursor);
  if (typeof filters.limit === 'number') sp.set('limit', String(filters.limit));
  if (filters.dir) sp.set('dir', filters.dir);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * GET /api/v1/admin/merchants/:merchantId/credit/history — chronological
 * cursor-paginated history (active + revoked + expired + overdue).
 */
export async function getMerchantCreditHistory(
  merchantId: string,
  filters: MerchantCreditHistoryFilters = {},
): Promise<MerchantCreditHistoryResponse> {
  const c = apiClient() as unknown as LooseClient;
  const path = `/api/v1/admin/merchants/${encodeURIComponent(merchantId)}/credit/history${buildHistoryQs(filters)}`;
  const { data, error, response } = await c.GET(path);
  if (error || !response.ok) {
    throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
  }
  const payload = unwrap<MerchantCreditHistoryResponse>(data);
  return {
    rows: payload.rows ?? [],
    nextCursor: payload.nextCursor ?? null,
    hasMore: payload.hasMore ?? false,
    total: payload.total ?? 0,
  };
}

function buildAdminListQs(filters: AdminCreditsListFilters): string {
  const sp = new URLSearchParams();
  if (filters.merchantId) sp.set('merchantId', filters.merchantId);
  if (filters.status) sp.set('status', filters.status);
  if (filters.q) sp.set('q', filters.q);
  if (typeof filters.page === 'number') sp.set('page', String(filters.page));
  if (typeof filters.pageSize === 'number') sp.set('pageSize', String(filters.pageSize));
  if (filters.sort) sp.set('sort', filters.sort);
  if (filters.dir) sp.set('dir', filters.dir);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * GET /api/v1/admin/credits — cross-merchant offset-paginated list. No
 * bulk operations ("Từng merchant — không bulk") — consumers
 * MUST NOT render multi-select checkboxes.
 */
export async function listAdminCredits(
  filters: AdminCreditsListFilters = {},
): Promise<AdminCreditsListResponse> {
  const c = apiClient() as unknown as LooseClient;
  const path = `/api/v1/admin/credits${buildAdminListQs(filters)}`;
  const { data, error, response } = await c.GET(path);
  if (error || !response.ok) {
    throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
  }
  const payload = unwrap<AdminCreditsListResponse>(data);
  return {
    rows: payload.rows ?? [],
    page: payload.page ?? 1,
    pageSize: payload.pageSize ?? 20,
    total: payload.total ?? 0,
    totalPages: payload.totalPages ?? 0,
  };
}
